import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Home, Clock, Target, Trophy, BookOpen,
  CheckCircle, XCircle, ChevronRight, ChevronLeft, Flag,
  Brain, Zap, Play, Pause, RefreshCw, BarChart3, Award,
  Sparkles, Loader2, AlertCircle, TrendingUp, TrendingDown,
  FileText, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Capability Tier Colors
const TIER_COLORS = {
  1: { bg: 'bg-slate-500', text: 'text-slate-400', name: 'Foundation' },
  2: { bg: 'bg-blue-500', text: 'text-blue-400', name: 'Secure' },
  3: { bg: 'bg-green-500', text: 'text-green-400', name: 'Greater Depth' },
  4: { bg: 'bg-amber-500', text: 'text-amber-400', name: 'Exam Ready' },
  5: { bg: 'bg-purple-500', text: 'text-purple-400', name: 'Stretch / Scholarship' }
};

// Map stage params to exam types
const STAGE_TO_EXAM_TYPE = {
  '11-plus': '11-plus',
  '11plus': '11-plus',
  'eleven-plus': '11-plus',
  'gcse': 'gcse',
  'year-10': 'gcse',
  'a-level': 'a-level',
  'alevel': 'a-level',
  '7-plus': '7-plus',
  '7plus': '7-plus'
};

// Map exam board keywords to config name patterns
const EXAM_BOARD_PATTERNS = {
  'sutton': 'Sutton SET',
  'gl': 'GL Assessment',
  'cem': 'CEM',
  'iseb': 'ISEB',
  'aqa': 'AQA',
  'edexcel': 'Edexcel',
  'ocr': 'OCR'
};

const AssessmentFactory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('home'); // 'home', 'config', 'exam', 'results'
  const [mockType, setMockType] = useState('ai'); // 'ai' or 'premade'
  const [loading, setLoading] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [examConfigs, setExamConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [currentMock, setCurrentMock] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [mockResults, setMockResults] = useState(null);
  const [mockHistory, setMockHistory] = useState([]);
  
  // Get stage and subject from URL params (passed from LearningEngine)
  const urlStage = searchParams.get('stage');
  const urlSubject = searchParams.get('subject');
  // Optional board scope. Surfaces a small banner above the mock grid when
  // the user lands here from a BoardPracticeBrowser deep-link. The mock
  // catalogue itself is no longer windowed — the Board → Subject → Paper
  // drill-down already picks the right paper via ?paper=N and AutoLaunches.
  const urlBoard = (searchParams.get('board') || '').toLowerCase();
  const BOARD_LABELS = {
    sutton: 'Sutton SET',
    gl: 'GL Assessment',
    cem: 'CEM',
    iseb: 'Independent Schools (ISEB)',
    aqa: 'AQA GCSE',
    edexcel: 'Edexcel GCSE',
    ocr: 'OCR GCSE',
  };
  const boardTitle = urlBoard
    ? `${BOARD_LABELS[urlBoard] || urlBoard.toUpperCase()}${urlBoard === 'sutton' ? ` — Stage ${urlStage || '1'}` : ''}`
    : null;

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    loadInitialData();
  }, [token, urlStage, urlSubject]);

  // Auto-launch a specific paper when the BoardPracticeBrowser deep-links
  // here with ?paper=N. Without this, the user would land on the mock grid
  // and have to click the paper again.
  useEffect(() => {
    const paperParam = searchParams.get('paper');
    if (paperParam && token) {
      const n = parseInt(paperParam, 10);
      if (n >= 1 && n <= 30) {
        loadPremadeMock(n);
      }
    }
  }, [searchParams.get('paper'), token]);

  // Timer Effect
  useEffect(() => {
    let interval;
    if (isTimerRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            handleSubmitMock();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timeRemaining]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Priority: URL params > user profile > localStorage
      let examTypeFilter = '';
      let subjectFilter = '';
      
      // Get user data for exam board preference
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userExamBoard = user.examBoard || user.studentProfile?.examBoard || ''; // e.g., 'sutton', 'gl', 'aqa'
      // Read every possible field — examFocus, examGoal, examTarget — plus
      // yearGroup so 7+/11+/Year 9/GCSE students all get matched correctly.
      const userExamFocus = (
        user.examFocus ||
        user.studentProfile?.examFocus ||
        user.studentProfile?.examGoal ||
        user.studentProfile?.examTarget ||
        user.studentProfile?.learningGoal ||
        ''
      );
      const userYearGroup = (
        user.year_group ||
        user.yearGroup ||
        user.studentProfile?.yearGroup ||
        ''
      );
      
      // First check URL params (from Learning Engine)
      if (urlStage) {
        examTypeFilter = STAGE_TO_EXAM_TYPE[urlStage.toLowerCase()] || urlStage;
      }
      if (urlSubject) {
        subjectFilter = urlSubject.toLowerCase();
      }
      
      // Fallback to user's stored stage/exam focus
      if (!examTypeFilter && userExamFocus) {
        const f = userExamFocus.toLowerCase();
        if (f.includes('11') || f.includes('eleven')) {
          examTypeFilter = '11-plus';
        } else if (f.includes('gcse') || f.includes('year-10') || f.includes('year-11')) {
          examTypeFilter = 'gcse';
        } else if (f.includes('a-level') || f.includes('alevel')) {
          examTypeFilter = 'a-level';
        } else if (f.includes('7')) {
          examTypeFilter = '7-plus';
        }
      }

      // Final fallback: derive from yearGroup (e.g., "Year 2" → 7-plus,
      // "Year 5" → 11-plus, "Year 10" → gcse). Word-boundary regex avoids
      // "Year 10" being matched by the "Year 1" check.
      if (!examTypeFilter && userYearGroup) {
        const yg = userYearGroup.toLowerCase();
        if (/year[\s-]?1[01]\b/.test(yg)) examTypeFilter = 'gcse';
        else if (/year[\s-]?[789]\b/.test(yg)) examTypeFilter = 'gcse';
        else if (/year[\s-]?[56]\b/.test(yg)) examTypeFilter = '11-plus';
        else if (/year[\s-]?[12]\b/.test(yg)) examTypeFilter = '7-plus';
      }
      
      // Build config URL with filters
      let configUrl = `${API}/assessment-factory/exam-configs`;
      const params = new URLSearchParams();
      if (examTypeFilter) params.append('exam_type', examTypeFilter);
      if (subjectFilter) params.append('subject', subjectFilter);
      if (params.toString()) configUrl += `?${params.toString()}`;
      
      const [profileRes, configsRes, historyRes] = await Promise.all([
        axios.get(`${API}/assessment-factory/student-profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(configUrl, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/assessment-factory/mock-history?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStudentProfile(profileRes.data);
      const configs = configsRes.data.configs || [];
      setExamConfigs(configs);
      setMockHistory(historyRes.data.history || []);
      
      // Only auto-recommend if user has a specific exam board preference
      if (userExamBoard && configs.length > 0) {
        const boardPattern = EXAM_BOARD_PATTERNS[userExamBoard.toLowerCase()];
        if (boardPattern) {
          const matchingConfig = configs.find(c => 
            c.name.includes(boardPattern) && c.enabled
          );
          if (matchingConfig) {
            setSelectedConfig(matchingConfig);
            toast.success(`Ready for ${matchingConfig.name} - your preferred exam board!`);
          }
        }
      } else if (urlStage && urlSubject) {
        // If coming from learning path but no specific board, just show info
        toast.info(`Showing ${examTypeFilter} ${subjectFilter} mocks. Select your exam board below.`);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const generateMock = async (config) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/assessment-factory/generate-mock`, {
        exam_type: config.exam_type,
        exam_board: config.exam_board,
        subject: config.subject
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentMock(response.data);
      setAnswers({});
      setFlaggedQuestions(new Set());
      setCurrentQuestionIndex(0);
      setTimeRemaining(response.data.duration_minutes * 60);
      setView('exam');
      setIsTimerRunning(true);
      toast.success('Mock paper generated!');
    } catch (error) {
      console.error('Error generating mock:', error);
      toast.error('Failed to generate mock paper');
    } finally {
      setLoading(false);
    }
  };

  const loadPremadeMock = async (paperNumber) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/assessment-factory/premade-mock/${paperNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentMock(response.data);
      setAnswers({});
      setFlaggedQuestions(new Set());
      setCurrentQuestionIndex(0);
      setTimeRemaining(response.data.duration_minutes * 60);
      setView('exam');
      setIsTimerRunning(true);
      toast.success(`Mock ${paperNumber} loaded!`);
    } catch (error) {
      console.error('Error loading premade mock:', error);
      toast.error('Failed to load mock paper');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const toggleFlag = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmitMock = async () => {
    setIsTimerRunning(false);
    setLoading(true);
    
    try {
      // Use different endpoint for premade mocks
      const endpoint = currentMock.is_premade 
        ? `${API}/assessment-factory/submit-premade-mock/${currentMock.mock_id}`
        : `${API}/assessment-factory/submit-mock/${currentMock.mock_id}`;
      
      const response = await axios.post(
        endpoint,
        answers,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMockResults(response.data);
      setView('results');
      toast.success('Mock submitted successfully!');
    } catch (error) {
      console.error('Error submitting mock:', error);
      toast.error('Failed to submit mock');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = currentMock?.questions?.[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = currentMock?.total_questions || 0;

  // ============ HOME VIEW ============
  if (view === 'home') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 py-6 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-white/20" data-testid="back-btn">
                <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
              </Button>
              <Button variant="ghost" onClick={() => navigate('/categories')} className="text-white hover:bg-white/20">
                <Home className="mr-2 h-4 w-4" /> Home
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl">
                <span className="text-3xl">📝</span>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">Mock Exams</h1>
                <p className="text-purple-200">Practice makes perfect - test your knowledge</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-8">
          {/* Board-specific banner — surfaces which exam-board context the
              user opened the factory in so Sutton SET / GL / CEM / ISEB
              feel like distinct surfaces, not the same generic grid. */}
          {boardTitle && (
            <Card
              className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 shadow-sm"
              data-testid="board-banner"
            >
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Exam Board</p>
                  <h2 className="text-lg font-bold text-slate-900 mt-0.5" data-testid="board-banner-title">
                    {boardTitle}
                  </h2>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Pick a paper from the full catalogue, or jump back to choose by subject.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/practice-hub')}
                  className="border-violet-300 text-violet-700 hover:bg-violet-100"
                  data-testid="board-banner-switch"
                >
                  Switch board
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Pre-made Mock Papers Only */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                {boardTitle ? `${boardTitle} — Mock Papers` : 'Pre-made Mock Papers'}
              </CardTitle>
              <CardDescription className="text-gray-600">
                Full-length timed examinations designed to simulate real exam conditions. 
                No solutions shown during the test - review answers after completion.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30].map((paperNum) => {
                  // All mocks unlocked for testing
                  const isLocked = false;
                  // Real completion + best-score lookup. mockHistory entries
                  // are of shape { mock_id:'premade_mock_2', score:36.0, ... };
                  // we walk every history record matching this paper number
                  // and surface the BEST score (matches the "Best: X%" UX on
                  // /papers). Multiple attempts at the same paper are
                  // expected — we keep the highest.
                  const matching = (mockHistory || []).filter((h) =>
                    h && (h.mock_id === `premade_mock_${paperNum}` || h.paper_number === paperNum)
                  );
                  const isCompleted = matching.length > 0;
                  const attemptsCount = matching.length;
                  const bestScore = isCompleted
                    ? Math.round(matching.reduce((mx, h) => Math.max(mx, h.score || 0), 0))
                    : undefined;
                  const score = bestScore;
                  
                  return (
                    <Card
                      key={paperNum}
                      className={`transition-all border hover:shadow-md cursor-pointer hover:border-purple-500
                        ${isCompleted ? 'bg-green-50 border-green-300 ring-1 ring-green-300' : 'bg-white border-gray-200'}`}
                      onClick={() => loadPremadeMock(paperNum)}
                      data-testid={`premade-mock-${paperNum}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className={`${isCompleted ? 'bg-green-600' : 'bg-purple-600'} text-white rounded-lg w-12 h-12 flex items-center justify-center font-bold text-lg relative`}>
                            {paperNum}
                            {isCompleted && (
                              <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5 ring-2 ring-white">
                                <CheckCircle className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>
                          {isCompleted ? (
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px] px-1.5 py-0.5" data-testid={`premade-mock-${paperNum}-status`}>
                              Retake
                            </Badge>
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <h3 className="font-bold text-base mb-1 text-gray-900">
                          Mock {paperNum}
                        </h3>
                        {isCompleted && score !== undefined && (
                          <div className="flex items-center gap-1 mb-1" data-testid={`premade-mock-${paperNum}-score`}>
                            <Trophy className="h-3 w-3 text-yellow-500" />
                            <span className={`text-sm font-semibold ${score >= 70 ? 'text-green-600' : 'text-orange-500'}`}>
                              Best {score}%
                            </span>
                            {attemptsCount > 1 && (
                              <span className="text-[10px] text-gray-500 ml-1">· {attemptsCount} attempts</span>
                            )}
                          </div>
                        )}
                        <div className="space-y-1 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>45 mins</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            <span>25 questions</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  // ============ EXAM VIEW ============
  if (view === 'exam' && currentMock && currentQuestion) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Exam Header */}
        <div className="bg-white border-b border-gray-200 py-3 px-4 sticky top-0 z-10 shadow-sm">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                {currentMock.title}
              </Badge>
              <span className="text-gray-600 text-sm">
                Q {currentQuestionIndex + 1} of {totalQuestions}
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Timer */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                timeRemaining < 300 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-700'
              }`}>
                <Clock className="h-4 w-4" />
                <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
              </div>
              
              {/* Progress */}
              <div className="hidden md:flex items-center gap-2">
                <span className="text-sm text-gray-600">{answeredCount}/{totalQuestions}</span>
                <Progress value={(answeredCount / totalQuestions) * 100} className="w-24 h-2" />
              </div>
              
              <Button
                onClick={handleSubmitMock}
                disabled={loading}
                className="bg-green-600 hover:bg-green-500"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Question Navigator (Sidebar) */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <Card className="bg-white border-gray-200 shadow-sm sticky top-20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-gray-900 text-sm">Questions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2">
                    {currentMock.questions.map((q, idx) => {
                      const isAnswered = answers[q.question_id] !== undefined;
                      const isFlagged = flaggedQuestions.has(q.question_id);
                      const isCurrent = idx === currentQuestionIndex;
                      
                      return (
                        <button
                          key={q.question_id}
                          onClick={() => setCurrentQuestionIndex(idx)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                            isCurrent
                              ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                              : isAnswered
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          } ${isFlagged ? 'ring-2 ring-amber-400' : ''}`}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
                      Answered
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-gray-100 ring-2 ring-amber-400"></div>
                      Flagged
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Question Content */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                        {currentQuestion.topic}
                      </Badge>
                      <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                        Difficulty: {currentQuestion.difficulty}/10
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleFlag(currentQuestion.question_id)}
                      className={flaggedQuestions.has(currentQuestion.question_id) 
                        ? 'text-amber-500' 
                        : 'text-gray-400'
                      }
                    >
                      <Flag className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Question Text */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <p className="text-gray-900 text-lg leading-relaxed whitespace-pre-wrap">
                      {currentQuestion.question_text}
                    </p>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, idx) => {
                      const isSelected = answers[currentQuestion.question_id] === idx;
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => handleAnswerSelect(currentQuestion.question_id, idx)}
                          className={`w-full p-4 rounded-xl text-left transition-all ${
                            isSelected
                              ? 'bg-purple-50 border-2 border-purple-500 text-gray-900'
                              : 'bg-white border-2 border-gray-200 hover:border-purple-300 text-gray-700'
                          }`}
                        >
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                            isSelected ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {String.fromCharCode(65 + idx)}
                          </span>
                          {option}
                        </button>
                      );
                    })}
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
                      disabled={currentQuestionIndex === 0}
                      className="border-gray-300 text-gray-700"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    
                    {currentQuestionIndex < totalQuestions - 1 ? (
                      <Button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmitMock}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Submit Exam <CheckCircle className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ RESULTS VIEW ============
  if (view === 'results' && mockResults) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className={`py-8 px-4 bg-gradient-to-r ${
          mockResults.score >= 75 
            ? 'from-green-600 to-emerald-600' 
            : mockResults.score >= 50 
              ? 'from-amber-600 to-orange-600'
              : 'from-red-600 to-rose-600'
        }`}>
          <div className="max-w-4xl mx-auto text-center">
            <Award className="h-16 w-16 text-white/80 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Mock Completed!</h1>
            <div className="text-6xl font-bold text-white mb-2">{Math.round(mockResults.score)}%</div>
            <p className="text-white/80">
              {mockResults.correct_count} out of {mockResults.total_questions} correct
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="mb-8">
            {/* Recommendations */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mockResults.recommendations?.map((rec, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${
                    rec.priority === 'high' 
                      ? 'bg-red-50 border border-red-200' 
                      : rec.priority === 'medium'
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-green-50 border border-green-200'
                  }`}>
                    <p className="text-gray-700 text-sm">{rec.message}</p>
                  </div>
                ))}
                
                {mockResults.weak_topics?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm text-gray-500 mb-2">Topics to Review:</p>
                    <div className="flex flex-wrap gap-1">
                      {mockResults.weak_topics.map(topic => (
                        <Badge key={topic} className="bg-red-100 text-red-700">{topic}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Results */}
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-gray-900">Question Review</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockResults.results?.map((result, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      result.is_correct 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-gray-900 font-medium">Q{idx + 1}</span>
                      {result.is_correct ? (
                        <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Correct</Badge>
                      ) : (
                        <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" /> Incorrect</Badge>
                      )}
                    </div>
                    {!result.is_correct && (
                      <div className="mt-2 text-sm">
                        <p className="text-gray-500">Your answer: Option {String.fromCharCode(65 + (result.user_answer ?? -1))}</p>
                        <p className="text-green-600">Correct answer: Option {String.fromCharCode(65 + result.correct_answer)}</p>
                        {result.explanation && (
                          <p className="text-gray-700 mt-2 p-2 bg-gray-100 rounded">{result.explanation}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-center gap-4 mt-8">
            <Button variant="outline" onClick={() => { setView('home'); loadInitialData(); }} className="border-gray-300 text-gray-700">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
            </Button>
            <Button onClick={() => generateMock(selectedConfig || examConfigs[0])} className="bg-purple-600 hover:bg-purple-700">
              <RefreshCw className="h-4 w-4 mr-2" /> Try Another Mock
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
    </div>
  );
};

export default AssessmentFactory;
